import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_OG_IMAGE,
  NOT_FOUND_SEO,
  SHELL_SEO,
  canonicalUrl,
  isAppShellPath,
  jsonLdForPage,
  seoForPath,
  type SeoPage,
} from "@/lib/seo";

const JSON_LD_ID = "seo-jsonld";

function setMeta(attr: "name" | "property", key: string, content: string | null) {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (content === null) {
    existing?.remove();
    return;
  }
  const tag = existing ?? document.createElement("meta");
  tag.setAttribute(attr, key);
  tag.setAttribute("content", content);
  if (!existing) document.head.appendChild(tag);
}

function setCanonical(href: string | null) {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) {
    existing?.remove();
    return;
  }
  const tag = existing ?? document.createElement("link");
  tag.setAttribute("rel", "canonical");
  tag.setAttribute("href", href);
  if (!existing) document.head.appendChild(tag);
}

function setJsonLd(page: SeoPage) {
  // The prerendered tag has no id; there is only ever one, so adopt it.
  const existing =
    document.getElementById(JSON_LD_ID) ??
    document.head.querySelector('script[type="application/ld+json"]');
  const data = jsonLdForPage(page);
  if (!data) {
    existing?.remove();
    return;
  }
  const tag = existing ?? document.createElement("script");
  tag.id = JSON_LD_ID;
  tag.setAttribute("type", "application/ld+json");
  tag.textContent = JSON.stringify(data).replace(/</g, "\u003c");
  if (!existing) document.head.appendChild(tag);
}

/** Writes one page's tags into the live <head>. Exported for the test. */
export function applySeo(page: SeoPage) {
  document.title = page.title;
  if (!page.indexable) {
    setMeta("name", "robots", "noindex, nofollow");
    setMeta("name", "description", null);
    setCanonical(null);
    setMeta("property", "og:url", null);
    setJsonLd(page);
    return;
  }
  const url = canonicalUrl(page);
  setMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1");
  setMeta("name", "description", page.description);
  setCanonical(url);
  setMeta("property", "og:type", page.ogType ?? "website");
  setMeta("property", "og:title", page.title);
  setMeta("property", "og:description", page.description);
  setMeta("property", "og:url", url);
  setMeta("property", "og:image", DEFAULT_OG_IMAGE);
  setMeta("name", "twitter:title", page.title);
  setMeta("name", "twitter:description", page.description);
  setMeta("name", "twitter:image", DEFAULT_OG_IMAGE);
  setJsonLd(page);
}

/**
 * Keeps <head> in step with the route on client-side navigation. The build
 * already wrote the right tags into each public page's HTML (see lib/seo.ts);
 * this is for the visitor who arrives on "/" and clicks through to /pricing,
 * and for crawlers that render JavaScript and follow in-app links.
 *
 * Mounted once, above <Routes>. Its effect runs before any page's own
 * useDocumentTitle, so a signed-in page that sets a more specific title still
 * wins; one that sets nothing gets the plain brand title and noindex rather
 * than whatever the previous public page left behind.
 */
export function SeoHead() {
  const { pathname } = useLocation();
  useEffect(() => {
    applySeo(seoForPath(pathname) ?? (isAppShellPath(pathname) ? SHELL_SEO : NOT_FOUND_SEO));
  }, [pathname]);
  return null;
}
